// The play screen, over the sky's upper half (the sky itself belongs to the
// scene host): the board with the floating HUD (Visual Direction, round
// seven onward): game state top-left (in Apogee with Finish and the
// selection's points beside it), the full screen and menu buttons top-right, the word
// panel bottom center (a tap moves it to the top), pan edges, the puzzle detail panel, toasts,
// confirmations, the play menu tray, and the How to Play card before the
// level begins, once the sky has finished its trip up (arrived). A tap on
// the open sky quiets the top HUD behind the stars, and another brings it back.
import { constellationOf, scoreConstellation, spelledWord, type PlayLevel, type PlayState } from '@wits/core';
import { useEffect, useRef, useState } from 'react';
import { navigate, nightPath, notePlayNight, playNightOf } from '../../app/router';
import {
  acceptSettled,
  askFinish,
  askRemove,
  cancelConfirm,
  clearAll,
  clearLatest,
  closeDetail,
  closePlay,
  confirmAction,
  dismissHowToPlay,
  dismissToast,
  endGoalShine,
  isFinished,
  openPlay,
  playSession,
  selectMenuTab,
  setMenuOpen,
  skyArrived,
  submit,
  tap,
  toggleDetail,
  toggleMenuSection,
} from '../../systems/play/playSession';
import { finishConfirmText, removeConfirmText } from '../../systems/play/outcomeText';
import { canFullscreen, toggleFullscreen } from '../../systems/settings/fullscreen';
import { gameSettings, inCase, type Lettercase } from '../../systems/settings/gameSettings';
import { useStore } from '../../systems/store';
import type { PlayRoute } from '../../app/router';
import { Button, IconButton } from '../components/Button';
import { Icon } from '../components/Icon';
import { useIsPortrait, usePlatform } from '../components/usePlatform';
import { SkyBoard } from '../sky/SkyBoard';
import { GameStateBar } from './GameStateBar';
import { HowToPlayCard } from './HowToPlayCard';
import { PuzzleDetail } from './PuzzleDetail';
import { ScoreDetail } from './ScoreDetail';
import { PlayMenu } from './PlayMenu';
import { LandscapePrompt } from './LandscapePrompt';
import { WordPanel } from './WordPanel';
import './play.css';

export function PlayScreen({ route, arrived }: { route: PlayRoute; arrived: boolean }) {
  const s = useStore(playSession);
  const settings = useStore(gameSettings);
  const platform = usePlatform();
  const portrait = useIsPortrait();
  const topLeft = useRef<HTMLDivElement>(null);
  const topRight = useRef<HTMLDivElement>(null);
  // A tap on the open sky quiets the top HUD behind the stars, and another brings it back (Aaron, 2026-10-30).
  const [hudQuiet, setHudQuiet] = useState(false);

  // Tonight's address names no night: the one the play opened on is noted in the history entry for a reload after the rollover.
  const night = route.date ?? playNightOf(route.levelId);
  useEffect(() => {
    void openPlay(route.levelId, route.date ?? playNightOf(route.levelId));
    setHudQuiet(false);
    return () => closePlay();
  }, [route.levelId, route.date]);

  useEffect(() => {
    if (!route.date && s.night && !s.playtest) notePlayNight(route.levelId, s.night);
  }, [route.levelId, route.date, s.night, s.playtest]);

  useEffect(() => {
    if (arrived) skyArrived();
  }, [arrived]);

  useEffect(() => {
    if (!s.toast) return;
    const t = setTimeout(() => dismissToast(s.toast!.key), s.toast.brief ? 1400 : 2600);
    return () => clearTimeout(t);
  }, [s.toast]);

  const finished = isFinished(s);
  const level = s.level;
  const board = s.board;
  // The word panel shows only while a word is in progress.
  const noWord = !board || board.selection.length === 0;
  // The feedback spot: a toast, or else the instruction until the first star is selected.
  const feedback = s.toast ?? (s.hint ? { key: 'hint', text: s.hint, brief: false, hint: true } : null);
  const apogee = level?.mode === 'apogee';
  // A finished level has the whole HUD back.
  const quiet = hudQuiet && !finished;
  // An open goal or score panel stays open, quiet with the rest (Aaron, 2026-10-30).
  const toggleHud = () => setHudQuiet(!quiet);

  return (
    <div className="play" data-portrait={portrait && platform === 'phone' ? 'true' : undefined} data-hud={quiet ? 'quiet' : undefined}>
      {board && level && (
        <SkyBoard
          board={board}
          showLetterValues={apogee && settings.showStarValues}
          lettercase={settings.lettercase}
          justAccepted={s.justAccepted}
          onTapNode={tap}
          onTapConstellation={askRemove}
          onDoubleTapSky={clearAll}
          onTapSky={toggleHud}
          onAcceptSettled={acceptSettled}
        />
      )}
      {s.phase === 'loading' && <div className="play-loading quiet">Reading the sky…</div>}
      {s.phase === 'error' && (
        <div className="panel play-error">
          <p>{s.error}</p>
          <div className="play-error-actions">
            {s.errorRetry && (
              <Button variant="ghost" onClick={() => void openPlay(route.levelId, night)}>
                Try again
              </Button>
            )}
            <Button variant="primary" onClick={() => navigate(nightPath(route.date))}>
              Home
            </Button>
          </div>
        </div>
      )}

      <div ref={topLeft} className="hud hud-top-left" inert={quiet}>
        {level && board && s.play && (
          <GameStateBar level={level} board={board} play={s.play} detailOpen={s.detailOpen} onToggleDetail={toggleDetail} />
        )}
        {level && board && s.play && apogee && !finished && (
          <>
            <Button variant="ghost" className="finish-btn" onClick={askFinish} disabled={s.busy}>
              Finish
            </Button>
            <SelectionPoints level={level} board={board} />
          </>
        )}
        {level && board && s.play && level.mode === 'puzzle' && s.detailOpen && (
          <PuzzleDetail level={level} play={s.play} shine={s.goalShine} onShineEnd={endGoalShine} onClose={closeDetail} />
        )}
        {level && board && apogee && s.detailOpen && <ScoreDetail level={level} board={board} onClose={closeDetail} />}
      </div>

      <div ref={topRight} className="hud hud-top-right" inert={quiet}>
        {canFullscreen() && <IconButton icon="fullscreen" label="Full screen" className="panel fullscreen-button" onClick={() => void toggleFullscreen()} />}
        <IconButton icon="menu" label="Menu" className="panel" onClick={() => setMenuOpen(true)} />
      </div>

      {feedback && (
        <div key={feedback.key} className={`toast panel${feedback.brief ? ' is-brief' : ''}${'hint' in feedback ? ' is-hint' : ''}${noWord ? ' no-word' : ''}`} role="status">
          {feedback.text}
        </div>
      )}

      {board && level && !finished && (
        <WordPanel empty={noWord} topLeft={topLeft} topRight={topRight}>
          <IconButton icon="clear-latest" label="Clear latest" onClick={clearLatest} disabled={board.selection.length === 0} />
          <IconButton icon="clear-all" label="Clear all" onClick={clearAll} disabled={board.selection.length === 0} />
          <span className="word mono" aria-live="polite">
            {wordDisplay(board, settings.lettercase)}
          </span>
          <Button variant="primary" onClick={() => void submit()} disabled={board.selection.length === 0 || s.busy}>
            Submit
          </Button>
        </WordPanel>
      )}

      {s.confirm && (
        <div className="confirm-root" onClick={cancelConfirm}>
          <div className="panel confirm" role="alertdialog" onClick={(e) => e.stopPropagation()}>
            <p className="confirm-text">
              {s.confirm.kind === 'finish' ? finishConfirmText(s.confirm.final) : removeConfirmText(s.confirm.word)}
            </p>
            <div className="confirm-actions">
              <Button variant="ghost" onClick={cancelConfirm}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void confirmAction()}>
                {s.confirm.kind === 'finish' ? 'Finish' : 'Return it'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {portrait && platform === 'phone' && <LandscapePrompt />}

      {s.howToPlay && s.arrived && <HowToPlayCard onUnderstood={dismissHowToPlay} />}

      <PlayMenu
        open={s.menuOpen}
        tab={s.menuTab}
        sections={s.menuSections}
        level={level}
        board={board}
        play={s.play}
        extraWords={s.extraWords}
        playtest={s.playtest}
        onClose={() => setMenuOpen(false)}
        onSelectTab={selectMenuTab}
        onToggleSection={toggleMenuSection}
      />
    </div>
  );
}

/** What the selected stars would score as a constellation, a ringed planet's doubling included: quiet, beside Finish, while a word is in progress. */
function SelectionPoints({ level, board }: { level: PlayLevel; board: PlayState }) {
  if (board.selection.length === 0) return null;
  const points = scoreConstellation(level, constellationOf(board)).total;
  return <span className="selection-points mono">+{points}</span>;
}

function wordDisplay(board: Parameters<typeof spelledWord>[0], lettercase: Lettercase): string {
  const nodes = new Map(board.level.nodes.map((n) => [n.id, n]));
  const parts = board.selection.map((id) => {
    const n = nodes.get(id);
    return n ? inCase(n.letter, lettercase) : '';
  });
  return parts.join('') || ' ';
}

export { Icon };
